import { useCallback, useRef, useState } from "react";

import useDebouncedCallback from "../../../hooks/useDebouncedCallback";
import { useCheckAiInterviewQuestionEeocComplianceMutation } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useAiInterviewerContext } from "./AiInterviewerContext";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import {
  buildContentKey,
  COMPLIANCE_CHECK_DEBOUNCE_MS,
  getCriteria,
} from "./utils";

const useCheckEeocCompliance = (
  questionIndex: number
): {
  checkEeocCompliance: () => void;
  eeocWarning: boolean;
  loading: boolean;
} => {
  const aiInterviewer = useAiInterviewerContext();
  const { getValues } = useAiInterviewerFormContext();
  const [checkMutation, { loading }] =
    useCheckAiInterviewQuestionEeocComplianceMutation();
  const [eeocWarning, setEeocWarning] = useState(false);
  const lastCheckedContentRef = useRef<string>("");
  const lastWarningResultRef = useRef<boolean>(false);
  const featureEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:compliance_check"
  );

  const performCheck = useCallback(async () => {
    if (!featureEnabled) {
      return;
    }

    const formValues = getValues();
    const question = formValues.questions[questionIndex];

    if (!question?.question?.trim()) {
      setEeocWarning(false);
      return;
    }

    // Get criteria based on question type
    const criteria = getCriteria(question);

    // Build content key and check if it changed
    const followups = question.followups?.filter(Boolean) || [];
    const contentKey = buildContentKey(
      question.question,
      followups as string[],
      criteria
    );

    if (contentKey === lastCheckedContentRef.current) {
      // Content hasn't changed, restore previous warning result
      setEeocWarning(lastWarningResultRef.current);
      return;
    }

    try {
      const result = await checkMutation({
        variables: {
          aiInterviewerId: aiInterviewer.id,
          question: question.question,
          followups,
          criteria: criteria || undefined,
        },
      });

      // Check if content changed while we were waiting - discard stale result
      const currentFormValues = getValues();
      const currentQuestion = currentFormValues.questions[questionIndex];
      if (!currentQuestion) return;

      const currentFollowups = currentQuestion.followups?.filter(Boolean) || [];
      const currentContentKey = buildContentKey(
        currentQuestion.question || "",
        currentFollowups as string[],
        getCriteria(currentQuestion)
      );

      if (currentContentKey !== contentKey) {
        return; // Content changed - discard stale result
      }

      // Update last checked content after successful check
      lastCheckedContentRef.current = contentKey;

      const hasIssue =
        result.data?.checkAiInterviewQuestionEeocCompliance?.hasIssue ?? false;
      lastWarningResultRef.current = hasIssue;
      setEeocWarning(hasIssue);
    } catch {
      // Silently fail - don't block the user
      setEeocWarning(false);
    }
  }, [
    aiInterviewer.id,
    getValues,
    questionIndex,
    checkMutation,
    featureEnabled,
  ]);

  const debouncedCheck = useDebouncedCallback(
    performCheck,
    COMPLIANCE_CHECK_DEBOUNCE_MS
  );

  return {
    checkEeocCompliance: debouncedCheck,
    eeocWarning,
    loading,
  };
};

export default useCheckEeocCompliance;
